import type React from 'react';
import {useRef, useState} from 'react';
import {Box} from '@mui/material';

export type MatrixPoint = {id: string; label: string; x: number; y: number; color?: string};

type Props = {
    points: MatrixPoint[];
    xLabel: string;
    yLabel: string;
    /** Quadrant names: top-left, top-right, bottom-left, bottom-right. */
    quadrants: [string, string, string, string];
    min?: number;
    max?: number;
    /** Where the quadrants split (default: the middle). */
    split?: number;
    readOnly?: boolean;
    /** Called once a point is dropped (or after arrow-key moves), with the new position. */
    onMove?: (id: string, x: number, y: number) => void;
    ariaLabel: string;
};

const W = 420;
const H = 300;
const PAD = {l: 44, r: 12, t: 12, b: 36};
const PW = W - PAD.l - PAD.r;
const PH = H - PAD.t - PAD.b;

/** A 2×2 matrix whose points can be dragged, or moved with the arrow keys after focusing them. */
export const MatrixChart: React.FC<Props> = ({
    points, xLabel, yLabel, quadrants, min = 0, max = 100, split, readOnly, onMove, ariaLabel,
}) => {
    const svg = useRef<SVGSVGElement>(null);
    const [drag, setDrag] = useState<{id: string; x: number; y: number} | null>(null);
    const mid = split ?? (min + max) / 2;
    const span = max - min;
    const sx = (v: number) => PAD.l + (v - min) / span * PW;
    const sy = (v: number) => PAD.t + (1 - (v - min) / span) * PH;
    const clamp = (v: number) => Math.round(Math.max(min, Math.min(max, v)) * 10) / 10;

    const toDomain = (e: React.PointerEvent): [number, number] => {
        const r = svg.current!.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width * W;
        const py = (e.clientY - r.top) / r.height * H;
        return [clamp(min + (px - PAD.l) / PW * span), clamp(min + (1 - (py - PAD.t) / PH) * span)];
    };

    const shown = points.map(p => (drag?.id === p.id ? {...p, x: drag.x, y: drag.y} : p));
    const qx = [PAD.l + 8, sx(mid) + 8];
    const qy = [PAD.t + 16, sy(mid) + 16];

    return (
        <Box sx={{width: '100%', maxWidth: 520}}>
            <svg ref={svg} viewBox={`0 0 ${W} ${H}`} width="100%" role="group" aria-label={ariaLabel}
                 style={{touchAction: 'none', userSelect: 'none'}}
                 onPointerMove={e => {
                     if (!drag) return;
                     const [x, y] = toDomain(e);
                     setDrag({id: drag.id, x, y});
                 }}
                 onPointerUp={() => {
                     if (drag) onMove?.(drag.id, drag.x, drag.y);
                     setDrag(null);
                 }}>
                <rect x={PAD.l} y={PAD.t} width={PW} height={PH} fill="#F7F8FA" stroke="#D9DCE1"/>
                <line x1={sx(mid)} x2={sx(mid)} y1={PAD.t} y2={PAD.t + PH} stroke="#B9BEC7" strokeDasharray="4 4"/>
                <line x1={PAD.l} x2={PAD.l + PW} y1={sy(mid)} y2={sy(mid)} stroke="#B9BEC7" strokeDasharray="4 4"/>
                {quadrants.map((q, i) => (
                    <text key={q} x={qx[i % 2]} y={qy[Math.floor(i / 2)]} fontSize={12} fill="#7A7F8A" fontWeight={600}>{q}</text>
                ))}
                <text x={PAD.l + PW / 2} y={H - 6} fontSize={12} fill="#44474F" textAnchor="middle">{xLabel} →</text>
                <text x={12} y={PAD.t + PH / 2} fontSize={12} fill="#44474F" textAnchor="middle"
                      transform={`rotate(-90 12 ${PAD.t + PH / 2})`}>{yLabel} →</text>
                {shown.map(p => (
                    <g key={p.id} transform={`translate(${sx(p.x)} ${sy(p.y)})`}
                       tabIndex={readOnly ? -1 : 0} role="slider"
                       aria-label={`${p.label}: ${xLabel} ${p.x}, ${yLabel} ${p.y}`} aria-valuenow={p.y}
                       style={{cursor: readOnly ? 'default' : 'grab', outline: 'none'}}
                       onPointerDown={e => {
                           if (readOnly) return;
                           (e.target as Element).setPointerCapture?.(e.pointerId);
                           setDrag({id: p.id, x: p.x, y: p.y});
                       }}
                       onKeyDown={e => {
                           if (readOnly) return;
                           const step = span / 20;
                           const d = {ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step]}[e.key];
                           if (!d) return;
                           e.preventDefault();
                           const base = drag?.id === p.id ? drag : p;
                           setDrag({id: p.id, x: clamp(base.x + d[0]), y: clamp(base.y + d[1])});
                       }}
                       onKeyUp={e => {
                           if (drag?.id === p.id && e.key.startsWith('Arrow')) {
                               onMove?.(p.id, drag.x, drag.y);
                               setDrag(null);
                           }
                       }}>
                        <circle r={9} fill={p.color ?? '#2D6FF5'} stroke="#fff" strokeWidth={2}/>
                        <text x={12} y={4} fontSize={11} fill="#1F2229">{p.label}</text>
                    </g>
                ))}
            </svg>
        </Box>
    );
};
